import React from 'react';
import { useSelector } from 'react-redux';

import { isKnownRole } from '../../constants/rbac';
import type { RootState } from '../../store';

interface HomeEntryProps {
  /** The waiting homepage, for a signed-in account with a confirmed email and no role. */
  waitingHome: React.ReactNode;
  /** Today's homepage behind today's guard (RequirePermission search.own), for everyone else. */
  children: React.ReactNode;
}

// THE NO-ROLE LANDING AT / (29 Oct, the waiting homepage, plan 4.2 and 5). The ONE place that splits it, and only at /
// (the index route; every other route keeps RequirePermission and its waiting page).
//
// It sits inside SearchEntryAuthGuard, which keeps today's rules: nothing until Firebase has answered, signed out ->
// /sign-in. Then:
//   - signed in, NO role, email CONFIRMED  -> the waiting homepage;
//   - anything else                        -> children, exactly today's tree: RequirePermission search.own around Home.
//     So no role and NOT confirmed gets today's waiting page ("confirm your email"); a role that works only inside a
//     company and has none gets today's "needs a company" page (owner question Q2, plan default); every role holder,
//     confirmed or not, gets today's homepage.
// "No role" is role null, which the session bridge sets for an absent claim AND for an unknown role string (fail
// closed); a value that is not one of the seven roles is treated the same here (owner question Q3, plan default: the
// waiting homepage, whose Trending preview the gateway refuses to anything but an absent role).
//
// A ROLE REMOVED (plan 5): the gateway revokes the sessions, the next apiClient call signs the person out to "access
// changed", and the next sign-in lands here and is decided by the same rule. No other landing code exists.
//
// It reads state.auth ONLY. Every other slice is saved in the browser and may hold a former role holder's data; none of
// it may decide this, or show here.
const HomeEntry: React.FC<HomeEntryProps> = ({ waitingHome, children }) => {
  const auth = useSelector((state: RootState) => state.auth);

  if (!auth.authInitialized) {
    return null;
  }

  const noRole = auth.role === null || auth.role === undefined || !isKnownRole(auth.role);
  if (auth.isAuthenticated && noRole && auth.emailVerified === true) {
    return <>{waitingHome}</>;
  }

  return <>{children}</>;
};

export default HomeEntry;
